<script>
  import { getAbsoluteUrl } from "$lib/utils/urls.js";
  import { Block } from "@urbaninstitute/dataviz-components";
  import { fade } from "svelte/transition";
  import Chart from "./Chart.svelte";

  /**
   * @typedef {Object} SectionData
   * @property {string} title - section title
   * @property {string} dataFilter - the label to use to grab section values out of data
   * @property {string} illustrationSubtitle - subtitle that shows over illustration
   * @property {{type: string, value: string}[]} illustrationText - text content with illustration
   * @property {string} chartSubtitle - subtitle that shows over chart
   * @property {{type: string, value: string}[]} chartText - text content with chart
   * @property {string} chartNotes - notes for beneath the chart
   *
   */

  /**
   * @type {{sectionData: SectionData}}
   */
  let { sectionData } = $props();
  let isChartVisible = $state(true);
  let firstTextChunk = $state();
  let edgeOfObserving = 120;
  let width = $state(200);

  function onObserve(entries) {
    for (const entry of entries) {
      // toggle menu active state based on whether the trigger element is intersecting and above the viewport
      //only runs when first text is fully out of view
      //based on threshold zero, it will run when it goes from zero percent visible to any percent visible
      isChartVisible = entry.boundingClientRect.bottom < edgeOfObserving;
    }
  }
  let observerOptions = $derived({
    // Trigger when element is completely out of view
    rootMargin: `-${edgeOfObserving}px 0px 0px 0px`,
    threshold: 0
  });

  $effect(() => {
    if (firstTextChunk) {
      const observer = new IntersectionObserver(onObserve, observerOptions);
      observer.observe(firstTextChunk);
      return () => {
        observer.unobserve(firstTextChunk);
      };
    }
  });
</script>

<svelte:window bind:innerWidth={width} />
<Block width={width > 1160 ? "wide" : "body"}>
  <div class="section-container">
    {#if !isChartVisible || width < 1160}
      <div class="visuals illustration" out:fade>
        {#if sectionData.illustrationSubtitle}
          <h4>{sectionData.illustrationSubtitle}</h4>
        {/if}
        <img
          src={getAbsoluteUrl("/illustrations/cdfi-"+sectionData.dataFilter.toLowerCase()+".png")}
          alt="Illustration of buildings representing business clusters with data about {sectionData.dataFilter} microbusinesses."
          width="100%"
        />
      </div>
    {/if}
    {#if isChartVisible || width < 1160}
      <div class="visuals chart" out:fade>
        {#if sectionData.chartSubtitle }
          <h4>{sectionData.chartSubtitle}</h4>
        {/if}
        <Chart dataFilter={sectionData.dataFilter} />
      </div>
    {/if}

    <div class="text-content first-chunk" bind:this={firstTextChunk}>
      {#each sectionData.illustrationText as { value }}
        <p>{value}</p>
      {/each}
    </div>

    <div class="text-content second-chunk">
      {#each sectionData.chartText as { value }}
        <p>{value}</p>
      {/each}
    </div>
  </div>
</Block>

<style>
  .section-container {
    display: grid;
    grid-template-columns: 600px 80px 1fr;
    @media screen and (max-width: 1160px) {
      display: flex;
      flex-direction: column;
      margin: 48px 0;
    }
  }
  .visuals {
    grid-column: 1;
    grid-row-start: 1;
    grid-row-end: 3;
    position: sticky;
    align-self: start;
    top: 30px;
    &.chart {
      /* padding-top: var(--spacing-20); */
      width: 100%;
    }

    @media screen and (max-width: 1160px) {
      position: relative;
      display: block;
      top:auto;
      &.illustration {
        order: 1;
      }
      &.chart {
        order: 3;
      }
    }
  }
  .text-content {
    grid-column-start: 3;
    &.first-chunk {
      p {
        /* font-size: 24px; */
        /* line-height: var(--line-height-relaxed); */
      }
    }
    @media screen and (min-width: 1160px) {
      margin: 60vh 0 40vh 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      /* box-shadow: 0 4px 4px #00000040; */
      /* padding: 1rem 3rem; */
      /* outline: 1px solid var(--color-gray-shade-light); */
      &.second-chunk {
        margin: 20vh 0 60vh 0;
      }
    }
    @media screen and (max-width: 1160px) {
      grid-column: 1;
      height: fit-content;
      display: block;
      &.first-chunk {
        order: 2;
      }
      &.second-chunk {
        order: 4;
      }
    }
  }
</style>
